import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Box, Typography, Paper, Grid, Card, CardContent, Button, Chip, LinearProgress,
  Table, TableHead, TableBody, TableRow, TableCell, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, CircularProgress, Alert,
} from '@mui/material';
import toast from 'react-hot-toast';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const sessions = (n) => (n == null ? '—' : `${Number(n)} session${Number(n) === 1 ? '' : 's'}`);

export default function Credits() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reqOpen, setReqOpen] = useState(false);
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/v1/admin/credits`);
      setData(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load sessions');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const hasPending = (data?.requests || []).some((r) => r.status === 'pending');

  const submitRequest = async () => {
    if (amount && (!/^\d+$/.test(amount.trim()) || Number(amount) < 1)) {
      toast.error('Enter a whole number of sessions, or leave it blank');
      return;
    }
    setSubmitting(true);
    try {
      const { data: sent } = await axios.post(`${API_URL}/api/v1/admin/credit-requests`, {
        amount_sessions: amount ? Number(amount) : null,
        note: note || null,
      });
      toast.success(`Request sent to ${sent.sent_to || 'the platform administrator'}`);
      setReqOpen(false); setAmount(''); setNote('');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send request');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box>;
  if (!data) return null;

  const unlimited = data.unlimited;
  const pct = (!unlimited && data.granted > 0) ? Math.min((data.used / data.granted) * 100, 100) : 0;
  const out = !unlimited && data.remaining != null && data.remaining <= 0;

  return (
    <Box>
      <Typography variant="h4" sx={{ fontWeight: 800, mb: 0.5 }}>AI Sessions</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Your clinic&apos;s AI session allowance. Each visit uses one session from the moment its recording
        starts — discarding the visit afterwards does not give it back, and a note written by hand uses none.
        When sessions run out, new recordings pause until {data.funded_by || 'the platform administrator'} adds
        more — you can request more below.
      </Typography>

      {out && (
        <Alert severity="error" sx={{ mb: 3 }}>
          Your AI sessions are used up — new recordings are paused. Notes can still be written by hand.
          Request more below.
        </Alert>
      )}

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={8}>
          <Card elevation={0} sx={{ border: '1px solid rgba(15,23,42,0.08)', borderRadius: 3 }}>
            <CardContent>
              {unlimited ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                  <Chip color="success" label="Unlimited" />
                  <Typography>Your clinic has unlimited AI sessions. Used so far: {sessions(data.used)}.</Typography>
                </Box>
              ) : (
                <>
                  <Typography variant="h5" fontWeight={800}>
                    {data.remaining} <Typography component="span" color="text.secondary" variant="h6">session{Number(data.remaining) === 1 ? '' : 's'} remaining</Typography>
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Used {data.used} of {data.granted} granted
                  </Typography>
                  <LinearProgress variant="determinate" value={pct} color={out ? 'error' : (pct >= 80 ? 'warning' : 'primary')} sx={{ height: 10, borderRadius: 5 }} />
                </>
              )}
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card elevation={0} sx={{ border: '1px solid rgba(15,23,42,0.08)', borderRadius: 3, height: '100%' }}>
            <CardContent sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', height: '100%' }}>
              <Button variant="contained" disabled={hasPending} onClick={() => setReqOpen(true)}>
                {hasPending ? 'Request pending…' : 'Request more sessions'}
              </Button>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                Requests go to {data.funded_by || 'the platform administrator'}.
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Paper elevation={0} sx={{ border: '1px solid rgba(15,23,42,0.08)', borderRadius: 3, overflow: 'hidden', mb: 3 }}>
        <Typography variant="subtitle1" fontWeight={700} sx={{ p: 2.5, pb: 1 }}>Usage by provider</Typography>
        <Table size="small">
          <TableHead><TableRow><TableCell>Provider</TableCell><TableCell align="right">Sessions</TableCell></TableRow></TableHead>
          <TableBody>
            {data.providers.length === 0 && <TableRow><TableCell colSpan={2} align="center" sx={{ color: 'text.secondary', py: 3 }}>No AI usage yet.</TableCell></TableRow>}
            {data.providers.map((p, i) => (
              <TableRow key={i}><TableCell>{p.name}</TableCell><TableCell align="right">{p.sessions}</TableCell></TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      <Paper elevation={0} sx={{ border: '1px solid rgba(15,23,42,0.08)', borderRadius: 3, p: 2.5 }}>
        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>Your session requests</Typography>
        {data.requests.length === 0 && <Typography variant="body2" color="text.secondary">No requests yet.</Typography>}
        {data.requests.map((r) => (
          <Box key={r.id} sx={{ display: 'flex', gap: 1, alignItems: 'center', py: 0.75, borderTop: '1px solid #00000010' }}>
            <Typography variant="body2" sx={{ flex: 1 }}>
              {(r.created_at || '').slice(0, 10)} — {r.amount_sessions != null ? sessions(r.amount_sessions) : 'amount unspecified'}{r.note ? ` · "${r.note}"` : ''}
              {r.review_note ? ` · reply: "${r.review_note}"` : ''}
            </Typography>
            <Chip size="small" label={r.status}
              color={r.status === 'approved' ? 'success' : r.status === 'declined' ? 'error' : 'warning'} />
          </Box>
        ))}
      </Paper>

      <Dialog open={reqOpen} onClose={() => setReqOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Request more AI sessions</DialogTitle>
        <DialogContent>
          <TextField fullWidth type="number" label="Sessions requested" sx={{ mt: 1 }} value={amount}
            onChange={(e) => setAmount(e.target.value)} inputProps={{ min: 0, step: '1' }} placeholder="e.g. 50" />
          <TextField fullWidth multiline minRows={2} label="Note (optional)" sx={{ mt: 2 }} value={note}
            onChange={(e) => setNote(e.target.value)} placeholder="Why you need more sessions" />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReqOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={submitRequest} disabled={submitting}>{submitting ? 'Sending…' : 'Send request'}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
